import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  structuredData?: object;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "結婚式写真共有アプリ - Photo Memorial | 式場向けプロフェッショナルソリューション",
  description = "参列者がQRコードで簡単アクセス。結婚式の写真・コメントをリアルタイムで会場スクリーンに投影。式場運営に最適化されたプロフェッショナルな写真共有ソリューション。導入実績300+会場、満足度98%。",
  keywords = "結婚式, 写真共有, QRコード, スクリーン投影, 式場運営, ウェディング, リアルタイム, モデレーション, 写真アプリ, 結婚式場",
  ogImage = "/og-image.jpg",
  ogUrl = "https://photo-memorial.com",
  structuredData
}) => {
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Photo Memorial",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "description": description,
    "offers": {
      "@type": "Offer",
      "price": "80000",
      "priceCurrency": "JPY",
      "description": "基本プラン（50名まで）"
    },
    "provider": {
      "@type": "Organization",
      "name": "株式会社Photo Memorial",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "JP",
        "addressLocality": "東京都",
        "addressRegion": "千代田区",
        "postalCode": "100-0001",
        "streetAddress": "千代田1-1-1"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+81-120-123-456",
        "contactType": "customer service",
        "availableLanguage": "Japanese"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "300",
      "bestRating": "5"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "田中美咲・健太郎"
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5"
        },
        "reviewBody": "ゲストの皆さんが撮影してくれた写真をすべて見ることができて、私たちが知らない素敵な瞬間もたくさん発見できました。"
      }
    ]
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* 基本メタタグ */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="株式会社Photo Memorial" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* 言語設定 */}
      <html lang="ja" />
      <meta httpEquiv="content-language" content="ja" />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:site_name" content="Photo Memorial" />
      <meta property="og:locale" content="ja_JP" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Apple Touch Icon */}
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      
      {/* テーマカラー */}
      <meta name="theme-color" content="#c7aa7a" />
      <meta name="msapplication-TileColor" content="#c7aa7a" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={ogUrl} />
      
      {/* DNS prefetch for performance */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      
      {/* Preconnect for faster loading */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* 構造化データ */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
      
      {/* アクセシビリティ */}
      <meta name="color-scheme" content="light" />
      
      {/* セキュリティ */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-Frame-Options" content="DENY" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
      
      {/* PWA対応 */}
      <link rel="manifest" href="/manifest.json" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Photo Memorial" />
    </Helmet>
  );
};

export default SEOHead;